import React from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { layering } from '@mobi/component-library/Theme/Common'
import { addToast, removeToast } from '@mobi/component-library/Feedback/Toast'
import type { ToastItem } from '@mobi/component-library/Feedback/Toast/types'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import type { Ticket } from '@core/Areas/AccountActivity/types'
import { AppRoutes } from '@core/AppRoutes'
import { queryClient } from '@core/Data/ReactQuery'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { appHistory } from '@core/Utils/routing/history'
import { postMultipleSharesToBetLoop } from '@core/Areas/BetLoop/helpers/api'
import { ShareBetSelection } from '@core/Areas/BetLoop/Components/QuickShare/Components/ShareBetSelection'
import { betLoopPrincipleProfileQuery } from '@core/Areas/BetLoop/helpers/queries'
import { trackShareBetClick } from '@core/Areas/ShareBet/ShareBetOutgoing/Components/ShareWithExternal/analytics'
import type { TicketShare } from '@core/Areas/ShareBet/types'
import { FirstPostConfirmation } from '../Common/FirstPostConfirmation'
import { trackShareBetError } from '@core/Areas/ShareBet/helpers/analytics'

const enum LocalConstants {
  QuickShareToastId = 'bet-loop-quick-share',
}

export const QuickShare: React.FC<{ isOpen: boolean; onDismiss(): void }> = ({
  isOpen,
  onDismiss,
}) => {
  const selectedTicketsRef = React.useRef<SelectedTickets>(new Map())

  const [isFirstShareModalOpen, setIsFirstShareModalOpen] = React.useState(false)
  const [shouldHidePostButton, setShouldHidePostButton] = React.useState(false)
  const [ticketsToShare, setTicketsToShare] = React.useState<TicketsToShare>(() => new Set())

  React.useEffect(() => {
    if (isOpen) return
    return () => {
      selectedTicketsRef.current = new Map()
      setIsFirstShareModalOpen(false)
      setTicketsToShare(new Set())
      setShouldHidePostButton(false)
    }
  }, [isOpen])

  const { data: profileData, isLoading: isLoadingProfile } = useQuery(betLoopPrincipleProfileQuery)
  
  const { mutate: onPostToBetLoopMutate, isPending } = useMutation({
    mutationFn: () => postMultipleSharesToBetLoop({ ticketSequences: Array.from(ticketsToShare) }),
    onSuccess: response => {
      handleShareToBetLoopMutateResponse(response, selectedTicketsRef.current)
      queryClient.invalidateQueries({ queryKey: queryKeys.betLoopSharePendingTickets })
      queryClient.invalidateQueries(betLoopPrincipleProfileQuery)
    },
    onMutate() {
      setIsFirstShareModalOpen(false)
      onDismiss()
    },
    onError: handleShareToBetLoopMutateError,
  })

  const handleTicketClick = (ticketSequence: Ticket['ticketSequence'], ticket: Ticket) => {
    if (isPending) return
    setTicketsToShare(prevTickets => {
      const newTickets = new Set(prevTickets)
      if (newTickets.has(ticketSequence)) {
        newTickets.delete(ticketSequence)
        selectedTicketsRef.current.delete(ticketSequence)
      } else {
        newTickets.add(ticketSequence)
        selectedTicketsRef.current.set(ticketSequence, ticket)
      }
      return newTickets
    })
  }

  const handleEmptyState = () => setShouldHidePostButton(true)

  const handleShareToBetLoopButtonClick = () => {
    profileData?.shareCount === 0 ? setIsFirstShareModalOpen(true) : onPostToBetLoopMutate()
  }

  return createPortal(
    <Tray
      isOpen={isOpen}
      headerTitle='Pending'
      onDismiss={onDismiss}
      footerComponent={
        shouldHidePostButton ? null : (
          <Button
            size='md'
            isFullWidth
            color='primary'
            isLoading={isPending}
            onClick={handleShareToBetLoopButtonClick}
            disabled={ticketsToShare.size === 0 || isLoadingProfile}
          >
            Share {ticketsToShare.size > 1 ? `${ticketsToShare.size} Bets` : 'Bet'} to Bet Loop
          </Button>
        )
      }
    >
      <>
        <ShareBetSelection
          ticketsToShare={ticketsToShare}
          onEmptyState={handleEmptyState}
          onTicketClick={handleTicketClick}
        />

        {createPortal(
          <FirstPostConfirmation
            zIndex={layering.overlayHigh}
            isOpen={isFirstShareModalOpen}
            onConfirm={() => onPostToBetLoopMutate()}
            onCancel={() => setIsFirstShareModalOpen(false)}
          />,
          document.body
        )}
      </>
    </Tray>,
    document.body
  )
}

// =============
// Local Helpers
// =============

function handleShareToBetLoopMutateResponse(
  {
    successfulShares,
    failedShares,
    results,
  }: Awaited<ReturnType<typeof postMultipleSharesToBetLoop>>,
  selectedTickets: SelectedTickets
) {
  const hasSuccessfulPosts = successfulShares > 0
  const hasFailedPosts = failedShares > 0
  const wereAllPostsSuccessful = hasSuccessfulPosts && !hasFailedPosts

  // Track failed shares
  if (hasFailedPosts) {
    selectedTickets.forEach(ticket => {
      const matchedResult = results?.find(result => result.ticketSequence === ticket.ticketSequence)
      if (matchedResult?.success) return // Skip successful shares

      const errorMessage = matchedResult?.errorMessage || 'Oops, unable to share to Bet Loop'
      trackShareBetError({
        message: errorMessage,
        destination: 'Bet Loop',
        location: 'bet-loop',
        betType: ticket.productName,
      })
    })
  }

  if (hasSuccessfulPosts) {
    selectedTickets.forEach(ticket => {
      const matchedResult = results?.find(result => result.ticketSequence === ticket.ticketSequence)
      if (!matchedResult?.success) return
      const ticketShare: TicketShare = {
        contextType: 'ticket',
        share: { ticket, activeTab: 'pending', ticketSequence: ticket.ticketSequence },
      }
      trackShareBetClick({
        data: ticketShare,
        isCostShared: false,
        destination: 'Bet Loop',
        sourceLocation: 'bet-loop',
      })
    })
  }

  let button: ToastItem['button']
  const icon = wereAllPostsSuccessful ? 'success' : hasSuccessfulPosts ? 'warning' : 'error'
  const title = hasSuccessfulPosts
    ? `${successfulShares} Bet${successfulShares > 1 ? 's' : ''} shared to Bet Loop`
    : 'Oops, unable to share to Bet Loop'

  if (hasSuccessfulPosts) {
    queryClient.invalidateQueries({
      queryKey: queryKeys.betLoopSharedBetsFeedBase(),
      refetchActive: true,
    })
    button = {
      text: 'View',
      onClick() {
        appHistory.push(AppRoutes.BetLoopMyProfile)
        removeToast({ id: LocalConstants.QuickShareToastId })
      },
    }
  }

  addToast({
    id: LocalConstants.QuickShareToastId,
    timeoutMs: 8_000,
    title,
    icon,
    button,
    isDismissable: !button,
    theme: 'dark',
  })
}

function handleShareToBetLoopMutateError(error: Error) {
  trackShareBetError({
    message: error.message,
    destination: 'Bet Loop',
    location: 'bet-loop',
    betType: undefined,
  })

  addToast({
    id: 'bet-loop-quick-share--error',
    timeoutMs: 6_000,
    title: 'Oops, unable to share to Bet Loop',
    icon: 'error',
    theme: 'dark',
  })
}

// =====
// Types
// =====

type TicketsToShare = Set<Ticket['ticketSequence']>

type SelectedTickets = Map<Ticket['ticketSequence'], Ticket>
